import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SEO from '../components/SEO';
import { useTheme } from '../lib/theme/index.js';
import { api } from '../lib/api';
import { track } from '../lib/track';
import type { ProximityCamp } from '../types/search';
import { formatDistance } from '../lib/distance';
import { getCategoriesGroupedByType, supabase } from '../lib/supabase';
import type { CampCategory, Camp } from '../types/database';
import CategoryTags from '../components/ui/CategoryTags';
import { getCategoryIcons } from '../lib/categoryPlaceholders';
import { getCampImage } from '../lib/images';
import RotatingText from '../components/RotatingText';
import CampMap from '../components/CampMap';
import ClientOnly from '../components/ClientOnly';
import { searchZipCodes, getZipCodeInfo, extractUniqueZipCodesFromCamps, type ZipCodeInfo } from '../lib/zipCodeLookup';
import { searchStates, getStateCenter } from '../lib/stateCenters';
import { useMapZoom } from '../hooks/useMapZoom';
import { useMapCamps } from '../hooks/useMapCamps';
import { MagnifyingGlassIcon, MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSession } from '@supabase/auth-helpers-react';
import {
  AcademicCapIcon,
  FireIcon,
  PuzzlePieceIcon,
  PaintBrushIcon,
  GlobeAltIcon,
  FlagIcon,
  SparklesIcon,
  LightBulbIcon,
  TrophyIcon,
  BeakerIcon,
  HeartIcon,
  ChevronDownIcon,
  CalendarDaysIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';

/**
 * Alternative Homepage with Care.com/Airbnb-inspired split layout
 * 
 * Layout:
 * - Hero section with background pattern
 * - Left (40%): Scrollable camp results list
 * - Right (60%): Interactive map with sticky snapping behavior
 * 
 * This is the /map-search route
 */
// Category icon mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Academic Enrichment': <AcademicCapIcon className="w-4 h-4" />,
  'Adventure & Nature': <FireIcon className="w-4 h-4" />,
  'Animal Encounters': <PuzzlePieceIcon className="w-4 h-4" />,
  'Arts & Creativity': <PaintBrushIcon className="w-4 h-4" />,
  'Cultural & Language Immersion': <GlobeAltIcon className="w-4 h-4" />,
  'Leadership & Life Skills': <FlagIcon className="w-4 h-4" />,
  'Multi-Activity': <SparklesIcon className="w-4 h-4" />,
  'Special Interests & Hobbies': <LightBulbIcon className="w-4 h-4" />,
  'Sports & Fitness': <TrophyIcon className="w-4 h-4" />,
  'STEM & Innovation': <BeakerIcon className="w-4 h-4" />,
  'Wellness & Mindfulness': <HeartIcon className="w-4 h-4" />,
};

export default function AlternativeHomePage() {
  const { themeVariant } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const session = useSession();

  // Check for preview token in URL
  const previewToken = searchParams.get('preview');

  // Check for admin preview images override (from localStorage) - per camp
  // Initialize as empty Set to avoid hydration mismatch (will be populated in useEffect)
  const [adminPreviewImagesCamps, setAdminPreviewImagesCamps] = useState<Set<string>>(new Set());

  // Load and listen for changes to admin preview images setting
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleStorageChange = () => {
        const stored = localStorage.getItem('admin_preview_images_camps');
        setAdminPreviewImagesCamps(stored ? new Set(JSON.parse(stored)) : new Set());
      };
      
      // Load initial value
      handleStorageChange();
      
      // Listen for storage events (from other tabs/windows)
      window.addEventListener('storage', handleStorageChange);
      
      // Also listen for custom events (from same tab)
      window.addEventListener('adminPreviewImagesChanged', handleStorageChange);
      
      return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('adminPreviewImagesChanged', handleStorageChange);
      };
    }
  }, []);

  // Check if user is admin
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    if (session?.user?.id) {
      void supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        })
        .then(({ data }) => {
          setIsAdmin(!!data);
        })
        .catch((error: Error) => {
          console.error('Error checking admin status:', error);
        });
    }
  }, [session?.user?.id]);

  // State for filters
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [selectedDropdownIndex, setSelectedDropdownIndex] = useState<number>(-1);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [radius, setRadius] = useState(10);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number; city: string; state: string } | null>(null);
  const [locationRequestFailed, setLocationRequestFailed] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);
  const mapSectionRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [isMapSticky, setIsMapSticky] = useState(false);
  
  // USA center coordinates (geographic center of USA)
  const USA_CENTER = { lat: 39.8283, lng: -98.5795 };
  const USA_ZOOM = 4; // Zoom level to show entire USA
  
  // Use shared hook for map zoom calculation
  const mapZoom = useMapZoom(radius);

  // State for UI
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isRadiusOpen, setIsRadiusOpen] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false); // Mobile map overlay toggle
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [camps, setCamps] = useState<ProximityCamp[]>([]);
  const [hoveredCampId, setHoveredCampId] = useState<string | null>(null);
  const [campCount, setCampCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 20;
  
  // State for advanced filter categories
  const [categories, setCategories] = useState<Record<string, CampCategory[]>>({});
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Only fire search_performed when user has changed search/filters, not on initial load
  const searchPerformedInitialLoadRef = useRef(false);

  // Focus area categories (main chips)
  const focusAreaCategories = [
    'Academic Enrichment',
    'Adventure & Nature',
    'Animal Encounters',
    'Arts & Creativity',
    'Cultural & Language Immersion',
    'Leadership & Life Skills',
    'Multi-Activity',
    'Special Interests & Hobbies',
    'Sports & Fitness',
    'STEM & Innovation',
    'Wellness & Mindfulness',
  ];

  // Count only non-focus-area filters for the badge
  const advancedFilterCount = selectedCategories.filter(
    cat => !focusAreaCategories.includes(cat)
  ).length;
  
  // Map selected category names/IDs to actual category IDs for API call
  const getCategoryIdsForSearch = (): string[] => {
    const categoryIds: string[] = [];
    
    // Get all categories from the grouped structure
    const allCategories: CampCategory[] = [
      ...(categories.focus_area || []),
      ...(categories.format || []),
      ...(categories.age_group || []),
      ...(categories.support_needs || []),
      ...(categories.values || []),
      ...(categories.organization_type || []),
    ];
    
    // Map focus area category names to IDs
    selectedCategories.forEach(selected => {
      if (focusAreaCategories.includes(selected)) {
        // It's a focus area name, find the matching category
        const category = allCategories.find(cat => cat.name === selected);
        if (category) {
          categoryIds.push(category.id);
        }
      } else {
        // It's already an ID from advanced filters
        categoryIds.push(selected);
      }
    });
    
    return categoryIds;
  };
  
  // Load categories for advanced filters
  useEffect(() => {
    async function loadCategories() {
      const groupedCategories = await getCategoriesGroupedByType();
      if (groupedCategories) {
        setCategories(groupedCategories);
      }
      setCategoriesLoading(false);
    }
    loadCategories();
  }, []);
  
  // Handle scroll for sticky map behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!mapSectionRef.current || !heroRef.current) return;
      
      const heroBottom = heroRef.current.getBoundingClientRect().bottom;
      const mapTop = mapSectionRef.current.getBoundingClientRect().top;
      const headerHeight = 80; // Navigation height
      
      // When hero section has scrolled past header and map section reaches top
      // Snap the map to sticky full-screen mode
      if (heroBottom <= headerHeight && mapTop <= headerHeight + 1) {
        setIsMapSticky(true);
      } else if (heroBottom > headerHeight) {
        // Unlock when scrolling back up past the hero
        setIsMapSticky(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to request user location
  const requestUserLocation = async () => {
    setIsRequestingLocation(true);
    setLocationRequestFailed(false);
    setPermissionDenied(false);
    
    // Check if geolocation is supported
    if (!('geolocation' in navigator)) {
      console.log('❌ Geolocation not supported');
      setLocationRequestFailed(true);
      setIsRequestingLocation(false);
      return;
    }

    // Check permission status first (if Permissions API is available)
    if ('permissions' in navigator) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        
        if (permissionStatus.state === 'denied') {
          console.warn('⚠️ Geolocation permission already denied');
          setPermissionDenied(true);
          setLocationRequestFailed(true);
          setIsRequestingLocation(false);
          setShowPermissionHelp(true);
          return;
        }
        
        // Listen for permission changes
        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            // Permission was granted, try again
            setPermissionDenied(false);
            requestUserLocation();
          } else if (permissionStatus.state === 'denied') {
            setPermissionDenied(true);
            setLocationRequestFailed(true);
            setShowPermissionHelp(true);
          }
        };
      } catch (err) {
        console.warn('⚠️ Could not check permissions:', err);
        // Continue to try requesting location anyway
      }
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log('✅ Got position:', {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        
        const { latitude, longitude } = position.coords;
        
        // Fetch city and state using reverse geocoding
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('📍 [AlternativeHomePage] Reverse geocode data:', data);
          
          setUserLocation({
            lat: latitude,
            lng: longitude,
            city: data.city || 'your location',
            state: data.principalSubdivision || ''
          });
        } catch (error) {
          console.error('❌ Error getting city/state:', error);
          // Still set location even if reverse geocoding fails
          setUserLocation({
            lat: latitude,
            lng: longitude,
            city: 'your location',
            state: ''
          });
        }
        
        setLocationRequestFailed(false);
        setPermissionDenied(false);
        setShowPermissionHelp(false);
        setIsRequestingLocation(false);
      },
      (error) => {
        console.error('❌ Geolocation error:', {
          code: error.code,
          message: error.message,
          PERMISSION_DENIED: error.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
          TIMEOUT: error.TIMEOUT
        });
        
        // Provide specific feedback based on error type
        if (error.code === error.PERMISSION_DENIED) {
          console.warn('⚠️ User denied geolocation permission');
          setPermissionDenied(true);
          setShowPermissionHelp(true);
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          console.warn('⚠️ Position unavailable');
        } else if (error.code === error.TIMEOUT) {
          console.warn('⚠️ Geolocation timeout');
        }
        
        // Don't set a default location - show US view instead
        setLocationRequestFailed(true);
        setIsRequestingLocation(false);
      },
      {
        timeout: 15000, // 15 seconds
        maximumAge: 0, // Don't use cached - always get fresh
        enableHighAccuracy: true // Request accurate location
      }
    );
  };

  // Get user location on mount
  useEffect(() => {
    console.log('🔍 [AlternativeHomePage] Mounting, checking geolocation...');
    
    // Check permissions API if available (modern browsers)
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((result) => {
        console.log('🔐 Geolocation permission status:', result.state);
        
        if (result.state === 'denied') {
          console.warn('⚠️ Geolocation permission denied by user');
          setLocationRequestFailed(true);
          return;
        }
        
        // Permission is granted or prompt - try to get location
        requestUserLocation();
      }).catch((err) => {
        console.warn('⚠️ Could not check permissions:', err);
        // Fallback: just try to get location
        requestUserLocation();
      });
    } else {
      // Permissions API not available, just try to get location
      requestUserLocation();
    }
  }, []);

  // Get camp count immediately when radius/filters change (fast count query)
  useEffect(() => {
    let cancelled = false;
    
    async function getCount() {
      try {
        const categoryIds = getCategoryIdsForSearch();
        
        // If no location, use fetchFilteredCampsCount without radius/center
        if (!userLocation || locationRequestFailed) {
          const count = await api.camps.getFilteredCampsCount({
            search: searchQuery || '',
            categories: categoryIds,
            radius: null,
            center: null,
          });
          if (!cancelled) {
            console.log('🔍 [AlternativeHomePage] Count query (US view) returned:', count);
            setCampCount(count);
          }
        } else {
          const count = await api.camps.getFilteredCampsCount({
            search: searchQuery || '',
            categories: categoryIds,
            radius: radius,
            center: [userLocation.lat, userLocation.lng],
          });
          if (!cancelled) {
            console.log('🔍 [AlternativeHomePage] Count query returned:', count);
            setCampCount(count);
          }
        }
      } catch (err) {
        console.error('❌ [AlternativeHomePage] Count query failed', err);
        if (!cancelled) setCampCount(0);
      }
    }
    
    getCount();
    return () => { cancelled = true; };
  }, [userLocation, locationRequestFailed, radius, selectedCategories, categories, searchQuery]);

  // Proximity search when location/radius/filters change (reset pagination)
  useEffect(() => {
    let cancelled = false;
    
    async function run() {
      setIsSearching(true);
      setSearchError(null);
      setOffset(0);
      setHasMore(true);
      
      try {
        const categoryIds = getCategoryIdsForSearch();
        
        // If no location or location request failed, use fetchFilteredCamps (US view)
        if (!userLocation || locationRequestFailed) {
          console.log('🔍 [AlternativeHomePage] Searching all US camps (no location)');
          const effectiveLimit = searchQuery && searchQuery.trim() ? 100 : PAGE_SIZE;
          
          const results = await api.camps.fetchFilteredCamps({
            search: searchQuery || '',
            categories: categoryIds,
            radius: null,
            center: null,
            page: 1,
            pageSize: effectiveLimit,
            sortBy: 'name',
          });
          
          console.log('🔍 [AlternativeHomePage] US view search results:', results.length, 'camps');
          
          if (!cancelled) {
            const validCamps = results
              .filter((c: any) => c && c.id && c.name)
              .map((c: any) => ({
                ...c,
                distance_miles: null // No distance for US view
              }));
            
            setCamps(validCamps);
            setHasMore(validCamps.length === effectiveLimit);
            if (searchPerformedInitialLoadRef.current) {
              track('search_performed', {
                query: searchQuery || undefined,
                location: undefined,
                radius_miles: undefined,
                filters: categoryIds.length ? { categories: categoryIds } : undefined,
                results_count: validCamps.length,
              });
            } else {
              searchPerformedInitialLoadRef.current = true;
            }
            console.log('🔍 [AlternativeHomePage] State updated with', validCamps.length, 'camps (US view)');
          }
        } else {
          // Use proximity search when location is available
          console.log('🔍 [AlternativeHomePage] Starting proximity search with radius:', radius);
          // Use bigger limit when search is active to find more matches
          const effectiveLimit = searchQuery && searchQuery.trim() ? 100 : PAGE_SIZE;
          console.log('🔍 [AlternativeHomePage] Searching with:', {
            lat: userLocation.lat,
            lng: userLocation.lng,
            radius,
            categoryIds,
            categoryCount: categoryIds.length,
            searchQuery: searchQuery || null,
            limit: effectiveLimit,
          });
          const response = await api.camps.proximitySearch({
            latitude: userLocation.lat,
            longitude: userLocation.lng,
            radiusMiles: radius,
            filters: categoryIds.length > 0 ? { categories: categoryIds } : undefined,
            searchQuery: searchQuery && searchQuery.trim() ? searchQuery.trim() : undefined,
            limit: effectiveLimit,
            offset: 0,
          });
        console.log('🔍 [AlternativeHomePage] API response:', response);
        // Handle both { results: [] } and direct array responses
        const results = Array.isArray(response) 
          ? response 
          : (response?.results || []);
        console.log('🔍 [AlternativeHomePage] Search results:', results.length, 'camps');
        if (!cancelled) {
          // Filter out any invalid camps and ensure distance_miles is set
          // Note: Server-side search is now handling text filtering, so we don't need client-side filtering
          const validCamps = results
            .filter((c: any) => c && c.id && c.name)
            .map((c: any) => ({
              ...c,
              distance_miles: typeof c.distance_miles === 'number' 
                ? c.distance_miles 
                : (c.distance_miles ? parseFloat(c.distance_miles) || 0 : 0)
            }));
          
          console.log('🔍 [AlternativeHomePage] Valid camps after filtering:', validCamps.length);
          
          // Fetch images for camps that don't have them (RPC doesn't return camp_images)
          // Also fetch display_images flag for camps
          const campsToFetchData = validCamps.filter((c: any) => 
            !c.camp_images || !Array.isArray(c.camp_images) || c.camp_images.length === 0 ||
            c.display_images === undefined || c.display_images === null
          );
          if (campsToFetchData.length > 0) {
            console.log('🔍 [AlternativeHomePage] Fetching images and display flags for', campsToFetchData.length, 'camps');
            const campIds = campsToFetchData.map((c: any) => c.id);
            
            // Fetch images
            const { data: imagesData, error: imagesError } = await supabase
              .from('camp_images')
              .select('*')
              .in('camp_id', campIds);
            
            // Fetch display flags and plan_id from camps (RPC may not return these)
            const { data: campsData, error: campsError } = await supabase
              .from('camps')
              .select('id, display_images, display_logo, plan_id, preview_token')
              .in('id', campIds);
            
            if (!imagesError && imagesData) {
              // Group images by camp_id (normalize to string for consistent lookup)
              const imagesByCampId = imagesData.reduce((acc: any, img: any) => {
                const cid = String(img.camp_id);
                if (!acc[cid]) acc[cid] = [];
                acc[cid].push(img);
                return acc;
              }, {});
              
              // Group display flags and plan_id by camp_id
              const displayFlagsByCampId: Record<string, { display_images: boolean; display_logo: boolean; plan_id: string | null; preview_token: string | null }> = {};
              if (!campsError && campsData) {
                campsData.forEach((camp: any) => {
                  displayFlagsByCampId[String(camp.id)] = {
                    display_images: camp.display_images ?? true,
                    display_logo: camp.display_logo ?? true,
                    plan_id: camp.plan_id ?? null,
                    preview_token: camp.preview_token ?? null,
                  };
                });
              }
              
              // Add images, display flags, and plan_id to camps (so list shows images for paying camps)
              validCamps.forEach((camp: any) => {
                const cid = String(camp.id);
                if (imagesByCampId[cid]) {
                  camp.camp_images = imagesByCampId[cid];
                }
                if (displayFlagsByCampId[cid]) {
                  camp.display_images = displayFlagsByCampId[cid].display_images;
                  camp.display_logo = displayFlagsByCampId[cid].display_logo;
                  camp.plan_id = displayFlagsByCampId[cid].plan_id;
                  camp.preview_token = displayFlagsByCampId[cid].preview_token;
                }
              });
              console.log('🔍 [AlternativeHomePage] Images and display flags fetched and added to camps');
            } else if (imagesError) {
              console.warn('⚠️ [AlternativeHomePage] Error fetching images:', imagesError);
            }
          }
          
          // Force React to detect the state change by using a new array
          const newCamps = validCamps.map(c => ({ ...c }));
          setCamps(newCamps);
          if (searchPerformedInitialLoadRef.current) {
            track('search_performed', {
              query: searchQuery || undefined,
              location: userLocation?.city && userLocation?.state ? `${userLocation.city}, ${userLocation.state}` : undefined,
              radius_miles: radius ?? undefined,
              filters: categoryIds.length ? { categories: categoryIds } : undefined,
              results_count: newCamps.length,
            });
          } else {
            searchPerformedInitialLoadRef.current = true;
          }
          // Don't update count here - it's already updated by the count query above
          setHasMore(validCamps.length === PAGE_SIZE);
          console.log('🔍 [AlternativeHomePage] State updated with', newCamps.length, 'camps');
        }
        }
      } catch (err: any) {
        console.error('❌ [AlternativeHomePage] proximitySearch failed', err);
        if (!cancelled) {
          setSearchError(err?.message || 'Search failed');
          setCamps([]);
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [userLocation, locationRequestFailed, radius, selectedCategories, categories, searchQuery]);

  // Load more camps when scrolling near bottom
  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl || !userLocation) return;

    let scrollTimer: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (scrollTimer) return; // Throttle
      scrollTimer = setTimeout(() => {
        scrollTimer = null;
      }, 200);

      if (!hasMore || isLoadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = listEl;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        // Near bottom, load more
        setIsLoadingMore(true);
        const currentOffset = camps.length;
        const categoryIds = getCategoryIdsForSearch();
        // Use bigger limit when search is active
        const effectiveLimit = searchQuery && searchQuery.trim() ? 100 : PAGE_SIZE;
        api.camps.proximitySearch({
          latitude: userLocation.lat,
          longitude: userLocation.lng,
          radiusMiles: radius,
          filters: categoryIds.length ? { categories: categoryIds } : undefined,
          searchQuery: searchQuery && searchQuery.trim() ? searchQuery.trim() : undefined,
          limit: effectiveLimit,
          offset: currentOffset,
        }).then(async (response) => {
          // Handle both { results: [] } and direct array responses
          const results = Array.isArray(response) 
            ? response 
            : (response?.results || []);
          
          // Filter and validate new camps
          // Note: Server-side search is now handling text filtering
          const validCamps = results
            .filter((c: any) => c && c.id && c.name)
            .map((c: any) => ({
              ...c,
              distance_miles: typeof c.distance_miles === 'number' 
                ? c.distance_miles 
                : (c.distance_miles ? parseFloat(c.distance_miles) || 0 : 0)
            }));
          
          // Fetch images for camps that don't have them (RPC doesn't return camp_images)
          // Also fetch display_images flag for camps
          const campsToFetchData = validCamps.filter((c: any) => 
            !c.camp_images || !Array.isArray(c.camp_images) || c.camp_images.length === 0 ||
            c.display_images === undefined || c.display_images === null
          );
          if (campsToFetchData.length > 0) {
            const campIds = campsToFetchData.map((c: any) => c.id);
            
            // Fetch images
            const { data: imagesData, error: imagesError } = await supabase
              .from('camp_images')
              .select('*')
              .in('camp_id', campIds);
            
            // Fetch display flags and plan_id from camps (RPC may not return these)
            const { data: campsData, error: campsError } = await supabase
              .from('camps')
              .select('id, display_images, display_logo, plan_id, preview_token')
              .in('id', campIds);
            
            if (!imagesError && imagesData) {
              const imagesByCampId = imagesData.reduce((acc: any, img: any) => {
                const cid = String(img.camp_id);
                if (!acc[cid]) acc[cid] = [];
                acc[cid].push(img);
                return acc;
              }, {});
              
              const displayFlagsByCampId: Record<string, { display_images: boolean; display_logo: boolean; plan_id: string | null; preview_token: string | null }> = {};
              if (!campsError && campsData) {
                campsData.forEach((camp: any) => {
                  displayFlagsByCampId[String(camp.id)] = {
                    display_images: camp.display_images ?? true,
                    display_logo: camp.display_logo ?? true,
                    plan_id: camp.plan_id ?? null,
                    preview_token: camp.preview_token ?? null,
                  };
                });
              }
              
              validCamps.forEach((camp: any) => {
                const cid = String(camp.id);
                if (imagesByCampId[cid]) camp.camp_images = imagesByCampId[cid];
                if (displayFlagsByCampId[cid]) {
                  camp.display_images = displayFlagsByCampId[cid].display_images;
                  camp.display_logo = displayFlagsByCampId[cid].display_logo;
                  camp.plan_id = displayFlagsByCampId[cid].plan_id;
                  camp.preview_token = displayFlagsByCampId[cid].preview_token;
                }
              });
            }
          }
          
          setCamps(prev => [...prev, ...validCamps]);
          // Don't update count on lazy load - it should match the total from the count query
          setHasMore(validCamps.length === PAGE_SIZE);
        }).catch((err: any) => {
          console.error('loadMore failed', err);
        }).finally(() => {
          setIsLoadingMore(false);
        });
      }
    };

    listEl.addEventListener('scroll', handleScroll);
    return () => {
      listEl.removeEventListener('scroll', handleScroll);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [camps.length, hasMore, isLoadingMore, userLocation, radius, selectedCategories, categories, searchQuery]);

  // Use shared hook to fetch ALL camps for the map (without pagination)
  const { data: allCampsForMap = [] } = useMapCamps({
    searchQuery,
    selectedCategories: getCategoryIdsForSearch(),
    radius,
    mapCenter: userLocation ? [userLocation.lat, userLocation.lng] : null,
    userLocation,
    queryKeyPrefix: 'camps-for-map-v2',
    showAllCamps: !userLocation || locationRequestFailed // Show all camps when in USA view
  });
  
  // Fetch all unique cities from database for search suggestions
  const { data: allCities = [] } = useQuery(
    ['all-cities'],
    async () => {
      const { data, error } = await supabase
        .from('camps')
        .select('city, state')
        .eq('active', true)
        .not('city', 'is', null)
        .not('state', 'is', null);
      
      if (error) {
        console.error('Error fetching cities:', error);
        return [];
      }
      
      // Get unique city/state combinations
      const uniqueCitiesSet = new Set<string>();
      (data || []).forEach((camp: any) => {
        if (camp.city && camp.state) {
          uniqueCitiesSet.add(`${camp.city}, ${camp.state}`);
        }
      });
      
      return Array.from(uniqueCitiesSet).sort();
    },
    {
      staleTime: 1000 * 60 * 10, // Cache for 10 minutes
      cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    }
  );

  // Get search suggestions for dropdown
  const getSearchSuggestions = () => {
    const query = searchQuery.trim();
    
    // Only show suggestions after 2 characters
    if (query.length < 2) return { camps: [], cities: [], zipCodes: [], states: [] };
    
    const queryLower = query.toLowerCase();
    
    // Check if query is numeric (likely a ZIP code)
    const isNumericQuery = /^\d+$/.test(query);
    
    // Find matching cities from all cities in database (limit to 5, but only if not numeric query)
    const matchingCities = !isNumericQuery ? (allCities || [])
      .filter((city: string) => city.toLowerCase().includes(queryLower))
      .slice(0, 5) : [];
    
    // Find matching states (limit to 3, but only if not numeric query)
    const matchingStates = !isNumericQuery ? searchStates(query).slice(0, 3) : [];
    
    // Find matching camps (limit to 8, but only if not numeric query)
    const matchingCamps = !isNumericQuery ? camps
      .filter(camp => 
        camp.name?.toLowerCase().includes(queryLower) ||
        camp.city?.toLowerCase().includes(queryLower) ||
        camp.description?.toLowerCase().includes(queryLower)
      )
      .slice(0, 8) : [];
    
    // Find matching ZIP codes (limit to 5, prioritize if numeric)
    let matchingZipCodes: ZipCodeInfo[] = [];
    try {
      matchingZipCodes = searchZipCodes(query, 5);
      if (isNumericQuery) {
        console.log('🔍 [AlternativeHomePage] ZIP code search for:', query, 'found:', matchingZipCodes.length, 'results:', matchingZipCodes.map(z => z.zip));
      }
    } catch (error) {
      console.error('❌ [AlternativeHomePage] Error in searchZipCodes:', error);
      matchingZipCodes = [];
    }
    
    return {
      camps: matchingCamps,
      cities: matchingCities,
      zipCodes: matchingZipCodes,
      states: matchingStates,
    };
  };
  
  // Handle ZIP code selection
  const handleZipCodeSelect = (zipInfo: { zip: string; city: string; state: string; latitude: number; longitude: number }) => {
    console.log('📍 [AlternativeHomePage] ZIP code selected:', zipInfo);
    // Clear search query - we're doing a location search, not text search
    setSearchQuery('');
    setShowSearchDropdown(false);
    
    // Update map center to ZIP code location
    console.log('📍 [AlternativeHomePage] Updating location to:', zipInfo.latitude, zipInfo.longitude);
    setUserLocation({ 
      lat: zipInfo.latitude, 
      lng: zipInfo.longitude,
      city: zipInfo.city || '',
      state: zipInfo.state || ''
    });
    setRadius(25); // Default radius for ZIP code search (zoom will be updated automatically by hook)
  };
  
  // Handle state selection
  const handleStateSelect = (stateInfo: { name: string; lat: number; lng: number }) => {
    // Clear search query - we're doing a location search, not text search
    setSearchQuery('');
    setShowSearchDropdown(false);
    
    // Update map center to state center
    setUserLocation({ 
      lat: stateInfo.lat, 
      lng: stateInfo.lng,
      city: '',
      state: stateInfo.name
    });
    setRadius(100); // Larger radius for state search (zoom will be updated automatically by hook)
  };
  
  // Handle city selection
  const handleCitySelect = async (cityString: string) => {
    // Clear search query - we're doing a location search, not text search
    setSearchQuery('');
    setShowSearchDropdown(false);
    setSelectedDropdownIndex(-1);
    
    // Parse city and state
    const [city, state] = cityString.split(',').map(s => s.trim());
    
    try {
      // Use geocoding to get coordinates for the city
      const { geocodeAddress } = await import('../lib/geocoding');
      const result = await geocodeAddress(undefined, city, state, undefined);
      
      if (result && result.accuracy !== 'failed') {
        setUserLocation({ lat: result.latitude, lng: result.longitude });
        setRadius(10); // Zoom will be updated automatically by hook
      } else {
        console.error('Could not get coordinates for city:', cityString);
      }
    } catch (error) {
      console.error('Error geocoding city:', error);
    }
  };
  
  // Handle camp selection (only navigate on Enter, not click)
  const handleCampSelect = (camp: ProximityCamp | Camp, navigateToPage: boolean = false) => {
    if (navigateToPage) {
      // Navigate to camp page
      let slug = camp.slug;
      
      // Generate slug if it doesn't exist (same logic as CampCard)
      if (!slug && camp.name && camp.city && camp.state) {
        const stateSlug = camp.state.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        const citySlug = camp.city.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        const nameSlug = camp.name.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        slug = `${stateSlug}/${citySlug}/${nameSlug}`;
      }
      
      if (slug && slug.includes('/')) {
        navigate(`/camps/${slug}`);
      } else if (slug) {
        navigate(`/camps/${slug}`);
      } else {
        // Fallback to ID if we can't generate a slug
        navigate(`/camps/${camp.id}`);
      }
      setShowSearchDropdown(false);
      setSelectedDropdownIndex(-1);
    } else {
      // Just update search query and map on click
      setSearchQuery(camp.name);
      setShowSearchDropdown(false);
      setSelectedDropdownIndex(-1);
      if (camp.latitude && camp.longitude) {
        setUserLocation({ lat: camp.latitude, lng: camp.longitude });
        setRadius(25); // Zoom will be updated automatically by hook
      }
    }
  };
  
  return (
    <>
      <SEO
        title="Find Summer Camps Near You | CampWhiz"
        description="Discover the perfect summer camps for your kids. Search by location, activities, and age to find camps near you."
        url={location.pathname === '/' ? '/' : '/map-search'}
      />
      
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        [data-theme="${themeVariant}"] {
          background: white !important;
        }
        [data-theme="${themeVariant}"]::before {
          display: none !important;
        }
        main[role="main"] {
          padding-bottom: 0 !important;
          margin-bottom: 0 !important;
        }
        .site-footer {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
      `}} />
      
      {/* Hero Section - Full Image Background */}
      {/* ORIGINAL: <div ref={heroRef} className="mt-[80px] py-4 sm:py-6 lg:py-12 px-4 sm:px-6 lg:px-8 relative"> */}
      {/* ORIGINAL: <div className="max-w-[90rem] mx-auto"> */}
      {/* ORIGINAL: <div className="rounded-3xl relative overflow-hidden min-h-[400px] sm:min-h-[450px] lg:min-h-[500px]"> */}
      <div ref={heroRef} className="mt-[80px] relative w-full">
        <div className="w-full">
          <div className="relative overflow-hidden min-h-[320px] sm:min-h-[380px] lg:min-h-[420px] w-full">
            {/* Hero Background Image - Full Width */}
            {/* ORIGINAL: <div className="absolute inset-0 rounded-3xl overflow-hidden"> */}
            <div className="absolute inset-0 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&w=1600&q=80"
                alt=""
                className="w-full h-full object-cover"
                style={{
                  objectPosition: 'center 45%',
                }}
                onError={(e) => {
                  console.error('Image failed to load');
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              {/* Purple/Orange Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-900/40 via-purple-700/30 to-orange-600/20" />
              {/* Dark Overlay for Text Readability */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/20 to-black/10" />
            </div>
            
            {/* Hero Content - Left Aligned */}
            <div className="relative z-[2] h-full min-h-[320px] sm:min-h-[380px] lg:min-h-[420px] flex flex-col justify-start pt-[100px] sm:pt-[120px] lg:pt-[140px] px-4 sm:px-6 lg:px-8">
              <div className="max-w-7xl mx-auto w-full">
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 text-left text-white">
                  One Hub for<br />
                  <RotatingText 
                    words={['Summer', 'Winter', 'Enrichment']} 
                    className="inline-block"
                    typingSpeed={100}
                    deletingSpeed={50}
                    pauseTime={2000}
                  />{' '}Camp Planning
                </h1>
                <p 
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-0 text-left font-semibold text-white"
                >
                  Find. Sync. Track.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content: Split Layout on Desktop, Stacked on Mobile */}
      <div className="flex flex-col lg:flex-row w-full bg-white min-h-screen" data-theme={themeVariant}>
          {/* Left Panel: Camp Results - Full width on mobile, 40% on desktop */}
          <div className="w-full lg:w-2/5 flex flex-col bg-white lg:border-r border-gray-200 min-h-screen">
            {/* Header Section - Sticky at top of left panel */}
            <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 flex-shrink-0 border-t border-b border-gray-200 bg-white flex items-center sticky top-0 z-50">
              <div className="flex items-center justify-between w-full gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
                      {campCount > 0 ? `${campCount} ` : ''}
                      {userLocation && !locationRequestFailed ? `Camps Near ${userLocation.city}, ${userLocation.state}` : 'Camps in the US'}
                    </h1>
                    {(!userLocation || locationRequestFailed) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          requestUserLocation();
                        }}
                        disabled={isRequestingLocation}
                        className="flex items-center gap-1.5 text-primary hover:text-primary/80 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MapPinIcon className="w-4 h-4" />
                        <span>Get my location</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {userLocation && !locationRequestFailed ? 'Sorted by distance' : locationRequestFailed ? 'Showing all camps' : isRequestingLocation ? 'Getting location...' : 'Loading location...'}
                  </p>
                  {permissionDenied && showPermissionHelp && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <strong>Location access denied.</strong> To enable location sharing:
                          <ul className="mt-1 ml-4 list-disc space-y-0.5">
                            <li><strong>Chrome/Edge:</strong> Click the lock icon (🔒) in address bar → Site settings → Location → Allow</li>
                            <li><strong>Firefox:</strong> Click the shield icon (🛡️) → Permissions → Location → Allow</li>
                            <li><strong>Safari:</strong> Safari → Settings → Websites → Location → Allow</li>
                          </ul>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPermissionHelp(false)}
                          className="text-amber-600 hover:text-amber-800 flex-shrink-0"
                          aria-label="Dismiss"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Mobile Map Toggle Button */}
                <button
                  onClick={() => setIsMapOpen(true)}
                  className="lg:hidden btn btn-primary btn-sm flex items-center gap-2 flex-shrink-0 min-h-[44px]"
                  aria-label="Show map"
                >
                  <MapPinIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Map</span>
                </button>
                
                {/* Radius Dropdown - Only show when location is available */}
                {userLocation && !locationRequestFailed && (
                  <div className="relative hidden sm:block">
                    <button
                      onClick={() => setIsRadiusOpen(!isRadiusOpen)}
                      className="btn btn-secondary flex items-center gap-2 px-4 py-3"
                    >
                      {radius} miles
                      <ChevronDownIcon className="w-5 h-5" />
                    </button>
                    
                    {isRadiusOpen && (
                      <>
                        {/* Backdrop */}
                        <div 
                          className="fixed inset-0 z-20" 
                          onClick={() => setIsRadiusOpen(false)}
                        />
                        
                        {/* Dropdown Menu */}
                        <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30">
                          {[10, 25, 50, 100, 250, 500].map((miles) => (
                            <button
                              key={miles}
                              onClick={() => {
                                setRadius(miles);
                                setIsRadiusOpen(false);
                              }}
                              className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors ${
                                radius === miles ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700'
                              }`}
                            >
                              {miles} miles
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Scrollable Camp Results Container */}
            <div ref={listRef} className="flex-1 overflow-y-auto">
              {/* Camp Results - List Format */}
              <div className="space-y-0 pt-0 pb-4">
              {isSearching && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-4 px-4 sm:px-6">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span>Searching camps nearby…</span>
                </div>
              )}
              {searchError && (
                <div className="text-sm text-red-600 py-4">
                  <p className="font-medium">Error loading camps:</p>
                  <p>{searchError}</p>
                </div>
              )}
              {!isSearching && !searchError && camps.length === 0 && userLocation && (
                <div className="text-sm text-gray-500 py-8 text-center">
                  <p>No camps found within {radius} miles.</p>
                  <p className="text-xs text-gray-400 mt-2">Try adjusting your search radius or filters.</p>
                </div>
              )}
              {!isSearching && !searchError && camps.length === 0 && !userLocation && (
                <div className="text-sm text-gray-500 py-8 text-center">
                  <p>Getting your location...</p>
                </div>
              )}
              {camps.map((camp, index) => {
                if (!camp || !camp.id || !camp.name) {
                  return null; // Skip invalid camps
                }

                // Get hero image (with safe checks - RPC might not return images)
                // Note: RPC may not return display_images field, so check if image exists
                const images = Array.isArray(camp.camp_images) ? camp.camp_images : [];
                
                // Debug logging for "the link" camp
                if (camp.name?.toLowerCase().includes('link')) {
                  console.log('🔍 [AlternativeHomePage] Camp "the link" debug:', {
                    campId: camp.id,
                    campName: camp.name,
                    hasImages: images.length > 0,
                    images: images,
                    display_images: camp.display_images,
                    photoImages: images.filter((img: any) => img?.type === 'photo'),
                    fullCamp: camp,
                  });
                }
                
                // Determine if we should show images (camp is paying OR has valid preview token OR admin preview override for this camp)
                // Admin preview override - shows all images to everyone for this specific camp regardless of payment status
                const adminOverride = adminPreviewImagesCamps.has(camp.id);
                // Check plan_id - must be truthy (not null, undefined, or empty string)
                const planId = camp?.plan_id;
                const isPaying = !!(planId && planId !== null && planId !== undefined && planId !== '');
                // Check preview token
                const urlPreviewToken = previewToken;
                const campPreviewToken = camp?.preview_token;
                const hasValidPreviewToken = !!(urlPreviewToken && campPreviewToken && urlPreviewToken === campPreviewToken);
                const shouldShowImages = adminOverride || isPaying || hasValidPreviewToken;
                
                // Debug logging for Planet camp
                if (process.env.NODE_ENV === 'development' && camp.name?.toLowerCase().includes('planet')) {
                  console.log('[AlternativeHomePage] Image visibility check:', {
                    campName: camp.name,
                    plan_id: planId,
                    plan_id_type: typeof planId,
                    isPaying,
                    previewToken: urlPreviewToken,
                    campPreviewToken,
                    hasValidPreviewToken,
                    shouldShowImages
                  });
                }

                // Try to get image - only show if display_images is true AND camp is paying/has preview token OR admin override
                const photoImage = images.find((img: any) => img?.type === 'photo');
                // Show image if:
                // 1. Admin override (shows all images regardless of flags), OR
                // 2. Camp is paying OR has valid preview token, AND
                // 3. Image exists, AND
                // 4. display_images is explicitly true (respects the toggle, unless admin override)
                // Default to true only if flag is undefined/null (backwards compatibility for old data)
                const shouldShowImage = adminOverride || (shouldShowImages && (camp.display_images === true || (camp.display_images === undefined || camp.display_images === null)));
                const heroImage = photoImage && shouldShowImage ? photoImage.url : null;
                const logoImage = images.find((img: any) => img?.type === 'logo')?.url;
                const shouldShowLogo = adminOverride || (shouldShowImages && (camp.display_logo === true || (camp.display_logo === undefined || camp.display_logo === null)));
                const finalLogoImage = logoImage && shouldShowLogo ? logoImage : null;
                
                // Check if image failed to load
                const imageFailed = heroImage ? failedImages.has(heroImage) : true;
                // Only paying/preview/admin: show real photo or default if no photo. Others: gradient.
                const displayImageUrl = shouldShowImages
                  ? (heroImage && !imageFailed ? heroImage : getCampImage(camp))
                  : null;

                // Get category icons for fancy gradient placeholder
                const categoryIcons = getCategoryIcons(camp.camp_category_relations?.map((r: any) => ({ name: r.camp_categories?.name || '' })) || []);
                
                // Create unique seed for gradient
                const campSeed = camp.id ? parseInt(camp.id.replace(/[^0-9]/g, '').slice(0, 8), 10) : 0;
                
                // Generate unique gradient for each card (same as CampCard)
                const gradientVariants = [
                  'linear-gradient(135deg, #C4B5FD 0%, #FED7AA 100%)', // Light purple to light orange
                  'linear-gradient(135deg, #DDD6FE 0%, #FED7AA 100%)', // Very light purple to light orange
                  'linear-gradient(135deg, #C4B5FD 0%, #FDBA74 100%)', // Light purple to soft orange
                ];
                const gradientIndex = campSeed % gradientVariants.length;
                const selectedGradient = gradientVariants[gradientIndex];

                const handleCardClick = () => {
                  // Camp routes use /camps/* with slug format: stateSlug/citySlug/nameSlug
                  if (camp.slug && camp.slug.includes('/')) {
                    // Camp already has a properly formatted slug
                    navigate(`/camps/${camp.slug}`);
                  } else if (camp.slug) {
                    // Camp has a slug but not in the expected format, try it anyway
                    navigate(`/camps/${camp.slug}`);
                  } else {
                    // Fallback: construct slug from camp data
                    const stateSlug = (camp.state || '').toLowerCase()
                      .replace(/[^a-z0-9\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .trim();
                    const citySlug = (camp.city || '').toLowerCase()
                      .replace(/[^a-z0-9\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .trim();
                    const nameSlug = (camp.name || '').toLowerCase()
                      .replace(/[^a-z0-9\s-]/g, '')
                      .replace(/\s+/g, '-')
                      .trim();
                    const constructedSlug = `${stateSlug}/${citySlug}/${nameSlug}`;
                    navigate(`/camps/${constructedSlug}`);
                  }
                };

                // Safe description handling
                const description = camp.description || '';
                const descriptionText = description.split('\n\n')[0] || description;
                const truncatedDescription = descriptionText.length > 100 
                  ? descriptionText.substring(0, 100) + '...' 
                  : descriptionText;

                return (
                  <div 
                    key={camp.id} 
                    onClick={handleCardClick}
                    onMouseEnter={() => setHoveredCampId(camp.id)}
                    onMouseLeave={() => setHoveredCampId(null)}
                    className="py-4 px-6 border-b border-gray-100 hover:bg-gray-50 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="flex items-start gap-4">
                      {/* Thumbnail with fancy gradient, icons, and hover effects */}
                      <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden relative">
                        {displayImageUrl ? (
                          <img
                            src={displayImageUrl}
                            alt={camp.name || 'Camp'}
                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                            onError={() => {
                              if (heroImage) setFailedImages(prev => new Set(prev).add(heroImage));
                            }}
                          />
                        ) : (
                          <div
                            className="w-full h-full relative overflow-hidden"
                            style={{
                              background: selectedGradient,
                              backgroundSize: '110% 110%',
                              backgroundPosition: 'center',
                              transition: 'background-position 0.5s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundPosition = '60% 60%';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundPosition = 'center';
                            }}
                          >
                            {categoryIcons.length > 0 && (
                              <div className="absolute inset-0 pointer-events-none">
                                {categoryIcons.slice(0, 2).map((iconConfig, index) => {
                                  const positions = [
                                    { bottom: '-0.75rem', right: '-0.75rem', size: 'w-16 h-16' },
                                    { bottom: '0.75rem', right: '1.5rem', size: 'w-10 h-10' },
                                  ];
                                  const position = positions[index % positions.length];
                                  return (
                                    <div
                                      key={index}
                                      className={`absolute ${position.size}`}
                                      style={{
                                        ...Object.fromEntries(
                                          Object.entries(position).filter(([key]) => key !== 'size')
                                        ),
                                      }}
                                    >
                                      <div
                                        className="w-full h-full flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110"
                                        style={{
                                          opacity: index === 0 ? 0.12 : 0.08,
                                          filter: 'blur(0.5px) drop-shadow(0 0 6px rgba(255, 255, 255, 0.15))',
                                        }}
                                      >
                                        {iconConfig.icon}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <div
                              className="absolute inset-0"
                              style={{
                                backgroundImage: `radial-gradient(circle, rgba(139, 92, 246, 0.08) 1px, transparent 1px)`,
                                backgroundSize: '5px 5px',
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors line-clamp-1">
                            {camp.name}
                          </h3>
                          {typeof camp.distance_miles === 'number' && !isNaN(camp.distance_miles) && (
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap flex-shrink-0">
                              {formatDistance(camp.distance_miles)}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          {camp.city && (
                            <span>{camp.city}{camp.state ? `, ${camp.state}` : ''}</span>
                          )}
                          {camp.min_age != null && camp.max_age != null && (
                            <>
                              <span>•</span>
                              <span>Ages {camp.min_age}-{camp.max_age}</span>
                            </>
                          )}
                        </div>

                        {description && (
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {truncatedDescription}
                          </p>
                        )}

                        {/* Category Tags - Compact */}
                        {Array.isArray(camp.camp_categories) && camp.camp_categories.length > 0 && (
                          <div className="mb-2">
                            <CategoryTags categories={camp.camp_categories} maxTags={2} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {isLoadingMore && (
                <div className="text-sm text-gray-500 py-4">Loading more camps...</div>
              )}
              {!hasMore && camps.length > 0 && (
                <div className="text-sm text-gray-500 py-4 text-center">No more camps to load</div>
              )}
              </div>
            </div>
          </div>
          
          {/* Right Panel: Filters + Map - Hidden on mobile, 60% on desktop */}
          <div 
            ref={mapSectionRef}
            className={`hidden lg:flex w-3/5 relative bg-white flex flex-col min-h-screen border-t border-gray-200 ${
              isMapSticky ? 'lg:sticky lg:top-0 lg:h-screen' : ''
            }`}
          >
            {/* Search & Filters at Top of Right Panel */}
            <div className="bg-white border-b border-gray-200 px-6 pt-6 pb-4 flex-shrink-0 flex items-center relative z-[1000] sticky top-0">
              <div className="w-full flex items-center gap-3">
                {/* Search Bar */}
                <div className="relative flex-1 z-[1000]">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search camps, cities, ZIP codes, or states…"
                      value={searchQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchQuery(value);
                        const shouldShow = value.trim().length >= 2;
                        setShowSearchDropdown(shouldShow);
                        setSelectedDropdownIndex(-1);
                        if (shouldShow) {
                          console.log('🔍 [AlternativeHomePage] Search query changed, showing dropdown:', value);
                        }
                      }}
                      onKeyDown={(e) => {
                        const { camps: suggestedCamps, cities: suggestedCities, zipCodes: suggestedZipCodes, states: suggestedStates } = getSearchSuggestions();
                        
                        const allItems: Array<{ type: 'zip' | 'state' | 'city' | 'camp'; data: any }> = [
                          ...suggestedZipCodes.map(z => ({ type: 'zip' as const, data: z })),
                          ...suggestedStates.map(s => ({ type: 'state' as const, data: s })),
                          ...suggestedCities.map(c => ({ type: 'city' as const, data: c })),
                          ...suggestedCamps.map(c => ({ type: 'camp' as const, data: c }))
                        ];
                        
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          if (!showSearchDropdown && searchQuery.trim().length >= 2) {
                            setShowSearchDropdown(true);
                          }
                          setSelectedDropdownIndex(prev => 
                            prev < allItems.length - 1 ? prev + 1 : 0
                          );
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setSelectedDropdownIndex(prev => 
                            prev > 0 ? prev - 1 : allItems.length - 1
                          );
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          if (selectedDropdownIndex >= 0 && selectedDropdownIndex < allItems.length) {
                            const selectedItem = allItems[selectedDropdownIndex];
                            if (selectedItem.type === 'zip') {
                              handleZipCodeSelect(selectedItem.data);
                            } else if (selectedItem.type === 'state') {
                              handleStateSelect(selectedItem.data);
                            } else if (selectedItem.type === 'city') {
                              handleCitySelect(selectedItem.data);
                            } else if (selectedItem.type === 'camp') {
                              handleCampSelect(selectedItem.data, true);
                            }
                          } else if (searchQuery.trim().length === 5 && /^\d{5}$/.test(searchQuery.trim())) {
                            const zipInfo = getZipCodeInfo(searchQuery.trim());
                            if (zipInfo) {
                              handleZipCodeSelect(zipInfo);
                            }
                          }
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setShowSearchDropdown(false);
                          setSelectedDropdownIndex(-1);
                        }
                      }}
                      onFocus={() => {
                        if (searchQuery.trim().length >= 2) {
                          setShowSearchDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          if (!document.activeElement?.closest('[role="listbox"]')) {
                            setShowSearchDropdown(false);
                            setSelectedDropdownIndex(-1);
                          }
                        }, 200);
                      }}
                      className="w-full px-4 py-3 pl-10 pr-4 border rounded-xl text-base form-field placeholder:text-gray-400 border-gray-300 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  
                  {/* Search Dropdown */}
                  {showSearchDropdown && searchQuery.trim().length >= 2 && (() => {
                    const { camps: suggestedCamps, cities: suggestedCities, zipCodes: suggestedZipCodes, states: suggestedStates } = getSearchSuggestions();
                    const hasSuggestions = suggestedCamps.length > 0 || suggestedCities.length > 0 || suggestedZipCodes.length > 0 || suggestedStates.length > 0;
                    
                    if (!hasSuggestions) return null;
                    
                    let currentIndex = 0;
                    const getItemIndex = () => currentIndex++;
                    
                    return (
                      <div 
                        className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-auto divide-y divide-gray-100"
                        role="listbox"
                        onMouseDown={(e) => e.preventDefault()}
                        style={{ 
                          position: 'absolute', 
                          top: '100%', 
                          left: 0, 
                          right: 0,
                          zIndex: 10001
                        }}
                      >
                        {suggestedZipCodes.length > 0 && (
                          <div>
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">ZIP Codes</div>
                            {suggestedZipCodes.map((zipInfo) => {
                              const itemIndex = getItemIndex();
                              const isSelected = selectedDropdownIndex === itemIndex;
                              return (
                                <button
                                  key={zipInfo.zip}
                                  onClick={() => handleZipCodeSelect(zipInfo)}
                                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-gray-100' : ''}`}
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <div className="flex-1">
                                    <span className="font-medium">ZIP {zipInfo.zip}</span>
                                    <div className="text-sm text-gray-500">View area</div>
                                  </div>
                                  <div className="text-sm text-gray-500">→</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {suggestedStates.length > 0 && (
                          <div>
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">States</div>
                            {suggestedStates.map((state) => {
                              const itemIndex = getItemIndex();
                              const isSelected = selectedDropdownIndex === itemIndex;
                              return (
                                <button
                                  key={state.name}
                                  onClick={() => handleStateSelect(state)}
                                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-gray-100' : ''}`}
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <div className="flex-1">
                                    <span className="font-medium">{state.name}</span>
                                  </div>
                                  <div className="text-sm text-gray-500">View area</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {suggestedCities.length > 0 && (
                          <div>
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">Cities</div>
                            {suggestedCities.map((city) => {
                              const itemIndex = getItemIndex();
                              const isSelected = selectedDropdownIndex === itemIndex;
                              return (
                                <button
                                  key={city}
                                  onClick={() => handleCitySelect(city)}
                                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-gray-100' : ''}`}
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <div className="flex-1">
                                    <span className="font-medium">{city}</span>
                                  </div>
                                  <div className="text-sm text-gray-500">View area</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {suggestedCamps.length > 0 && (
                          <div>
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">Camps</div>
                            {suggestedCamps.map((camp) => {
                              const itemIndex = getItemIndex();
                              const isSelected = selectedDropdownIndex === itemIndex;
                              return (
                                <button
                                  key={camp.id}
                                  onClick={() => handleCampSelect(camp, false)}
                                  className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${isSelected ? 'bg-gray-100' : ''}`}
                                  role="option"
                                  aria-selected={isSelected}
                                >
                                  <div className="font-medium line-clamp-1">{camp.name}</div>
                                  <div className="text-sm text-gray-500">{camp.city}, {camp.state}</div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                  
                {/* Filters Button */}
                <div className="relative">
                  <button
                    onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                    className={`whitespace-nowrap px-4 py-3 rounded-xl h-auto flex items-center gap-2 transition-colors ${
                      selectedCategories.length > 0
                        ? 'bg-primary text-white border-2 border-primary'
                        : 'btn btn-secondary'
                    }`}
                    style={{ height: 'auto', lineHeight: '1.5' }}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    Filters
                    {selectedCategories.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-white text-primary text-xs rounded-full font-semibold">
                        {selectedCategories.length}
                      </span>
                    )}
                  </button>
                  
                  {/* Popover Dropdown for Advanced Filters */}
                  {isFilterExpanded && (
                    <>
                      <div 
                        className="fixed inset-0 z-40" 
                        onClick={() => setIsFilterExpanded(false)}
                      />
                      <div 
                        className="absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 max-h-96 flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="overflow-y-auto flex-1">
                          <div className="py-1">
                            {categoriesLoading ? (
                              <div className="px-4 py-8 text-center text-gray-500">Loading filters...</div>
                            ) : (
                              <>
                                <div className="px-4 py-4">
                                  <h4 className="text-sm font-semibold text-gray-900 text-left mb-3">Categories</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {focusAreaCategories.map((category) => (
                                      <button
                                        key={category}
                                        onClick={() => {
                                          const isSelected = selectedCategories.includes(category);
                                          if (isSelected) {
                                            setSelectedCategories(selectedCategories.filter(c => c !== category));
                                          } else {
                                            setSelectedCategories([...selectedCategories, category]);
                                          }
                                        }}
                                        className={`category-chip ${
                                          selectedCategories.includes(category) 
                                            ? 'category-chip-selected' 
                                            : 'category-chip-default'
                                        }`}
                                      >
                                        {CATEGORY_ICONS[category]}
                                        <span className="ml-1.5">{category}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div className="border-t border-gray-300"></div>
                                {categories.format && categories.format.length > 0 && (
                                  <>
                                    <div className="px-4 py-4">
                                      <h4 className="text-sm font-semibold text-gray-900 text-left">Program Format</h4>
                                    </div>
                                    <div className="space-y-1">
                                      {categories.format.map((category) => (
                                        <button
                                          key={category.id}
                                          onClick={() => {
                                            const newCategories = selectedCategories.includes(category.id)
                                              ? selectedCategories.filter(id => id !== category.id)
                                              : [...selectedCategories, category.id];
                                            setSelectedCategories(newCategories);
                                          }}
                                          className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={selectedCategories.includes(category.id)}
                                            readOnly
                                            className="h-4 w-4 accent-primary border-gray-300 rounded focus:ring-primary"
                                          />
                                          <span className="ml-3">{category.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                    <div className="border-t border-gray-300"></div>
                                  </>
                                )}
                                {categories.age_group && categories.age_group.length > 0 && (
                                  <>
                                    <div className="px-4 py-4">
                                      <h4 className="text-sm font-semibold text-gray-900 text-left">Age Groups</h4>
                                    </div>
                                    {categories.age_group.map((category) => (
                                      <button
                                        key={category.id}
                                        onClick={() => {
                                          const newCategories = selectedCategories.includes(category.id)
                                            ? selectedCategories.filter(id => id !== category.id)
                                            : [...selectedCategories, category.id];
                                          setSelectedCategories(newCategories);
                                        }}
                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedCategories.includes(category.id)}
                                          readOnly
                                          className="h-4 w-4 accent-primary border-gray-300 rounded focus:ring-primary"
                                        />
                                        <span className="ml-3">{category.name}</span>
                                      </button>
                                    ))}
                                    <div className="border-t border-gray-300"></div>
                                  </>
                                )}
                                {categories.support_needs && categories.support_needs.length > 0 && (
                                  <>
                                    <div className="px-4 py-4">
                                      <h4 className="text-sm font-semibold text-gray-900 text-left">Support Needs</h4>
                                    </div>
                                    {categories.support_needs.map((category) => (
                                      <button
                                        key={category.id}
                                        onClick={() => {
                                          const newCategories = selectedCategories.includes(category.id)
                                            ? selectedCategories.filter(id => id !== category.id)
                                            : [...selectedCategories, category.id];
                                          setSelectedCategories(newCategories);
                                        }}
                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedCategories.includes(category.id)}
                                          readOnly
                                          className="h-4 w-4 accent-primary border-gray-300 rounded focus:ring-primary"
                                        />
                                        <span className="ml-3">{category.name}</span>
                                      </button>
                                    ))}
                                    <div className="border-t border-gray-300"></div>
                                  </>
                                )}
                                {categories.values && categories.values.length > 0 && (
                                  <>
                                    <div className="px-4 py-4">
                                      <h4 className="text-sm font-semibold text-gray-900 text-left">Values & Inclusion</h4>
                                    </div>
                                    {categories.values.map((category) => (
                                      <button
                                        key={category.id}
                                        onClick={() => {
                                          const newCategories = selectedCategories.includes(category.id)
                                            ? selectedCategories.filter(id => id !== category.id)
                                            : [...selectedCategories, category.id];
                                          setSelectedCategories(newCategories);
                                        }}
                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedCategories.includes(category.id)}
                                          readOnly
                                          className="h-4 w-4 accent-primary border-gray-300 rounded focus:ring-primary"
                                        />
                                        <span className="ml-3">{category.name}</span>
                                      </button>
                                    ))}
                                    <div className="border-t border-gray-300"></div>
                                  </>
                                )}
                                {categories.organization_type && categories.organization_type.length > 0 && (
                                  <>
                                    <div className="px-4 py-4">
                                      <h4 className="text-sm font-semibold text-gray-900 text-left">Organization Type</h4>
                                    </div>
                                    {categories.organization_type.map((category) => (
                                      <button
                                        key={category.id}
                                        onClick={() => {
                                          const newCategories = selectedCategories.includes(category.id)
                                            ? selectedCategories.filter(id => id !== category.id)
                                            : [...selectedCategories, category.id];
                                          setSelectedCategories(newCategories);
                                        }}
                                        className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={selectedCategories.includes(category.id)}
                                          readOnly
                                          className="h-4 w-4 accent-primary border-gray-300 rounded focus:ring-primary"
                                        />
                                        <span className="ml-3">{category.name}</span>
                                      </button>
                                    ))}
                                    <div className="border-t border-gray-300"></div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0 flex items-center justify-between">
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setSelectedCategories([]);
                              setRadius(25);
                              setIsFilterExpanded(false);
                            }}
                            className="text-sm text-primary hover:text-primary/80 underline transition-colors"
                          >
                            Clear all
                          </button>
                          <button
                            onClick={() => {
                              setIsFilterExpanded(false);
                            }}
                            className="btn btn-primary btn-sm"
                          >
                            Apply Filters
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Map Area Below Filters - Flexible, fills remaining space */}
            <div className={`flex-1 relative overflow-hidden ${
              isMapSticky ? 'h-[calc(100vh-140px)]' : 'min-h-[600px]'
            }`}>
              <ClientOnly fallback={
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading map...</p>
                  </div>
                </div>
              }>
                {(!userLocation || locationRequestFailed) ? (
                  <CampMap
                    camps={allCampsForMap as any}
                    center={[USA_CENTER.lat, USA_CENTER.lng]}
                    zoom={USA_ZOOM}
                    onCampSelect={(camp) => {
                      handleCampSelect(camp, true);
                    }}
                    onMapMove={(center) => {
                      // Don't update userLocation when in USA view
                    }}
                    hoveredCampId={hoveredCampId}
                    userLocation={null}
                    radius={null}
                  />
                ) : (
                  <CampMap
                    camps={allCampsForMap as any}
                    center={[userLocation.lat, userLocation.lng]}
                    zoom={mapZoom}
                    onCampSelect={(camp) => {
                      handleCampSelect(camp, true);
                    }}
                    onMapMove={(center) => {
                      setUserLocation({ lat: center[0], lng: center[1], city: userLocation.city, state: userLocation.state });
                    }}
                    hoveredCampId={hoveredCampId}
                    userLocation={[userLocation.lat, userLocation.lng]}
                    radius={radius}
                  />
                )}
              </ClientOnly>
            </div>
          </div>
      </div>

      {/* Mobile Map Overlay - Full Screen */}
      {isMapOpen && (
        <div className="fixed inset-0 z-[200] bg-white lg:hidden">
          {/* Header with Close Button */}
          <div className="absolute top-0 left-0 right-0 h-[73px] bg-white border-b border-gray-200 flex items-center justify-between px-4 z-10">
            <h2 className="text-lg font-semibold text-gray-900">Map</h2>
            <button
              onClick={() => setIsMapOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close map"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
          
          {/* Full-Screen Map */}
          <div className="absolute inset-0 pt-[73px]">
            <ClientOnly fallback={
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading map...</p>
                </div>
              </div>
            }>
              {(!userLocation || locationRequestFailed) ? (
                <CampMap
                  camps={allCampsForMap as any}
                  center={[USA_CENTER.lat, USA_CENTER.lng]}
                  zoom={USA_ZOOM}
                  hoveredCampId={hoveredCampId}
                  onCampSelect={(camp) => {
                    handleCampSelect(camp, true);
                    setIsMapOpen(false); // Close map after selecting camp
                  }}
                  onMapMove={(newCenter) => {
                    // Don't update userLocation when in USA view
                  }}
                  userLocation={null}
                  radius={null}
                />
              ) : (
                <CampMap
                  camps={allCampsForMap as any}
                  center={[userLocation.lat, userLocation.lng]}
                  zoom={mapZoom}
                  hoveredCampId={hoveredCampId}
                  onCampSelect={(camp) => {
                    handleCampSelect(camp, true);
                    setIsMapOpen(false); // Close map after selecting camp
                  }}
                  onMapMove={(newCenter) => {
                    setUserLocation({ lat: newCenter[0], lng: newCenter[1], city: userLocation.city, state: userLocation.state });
                  }}
                  userLocation={[userLocation.lat, userLocation.lng]}
                  radius={radius}
                />
              )}
            </ClientOnly>
          </div>
        </div>
      )}
    </>
  );
}
